import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { getUsers } from '../../services/api';
import { getTeamsByClient } from '../../services/teamService';
import { StandardTable } from '../common/UI/StandardTable';

export interface JobRecord {
  id: string;
  action_type: string;
  total_credits_burned: number;
  credits_burned?: number; // legacy
  original_amount: number;
  context_tag?: string;
  timestamp: unknown;
  startedAt?: unknown;
  updatedAt?: unknown;
  team_id?: string;
  user_id?: string;
  status?: string;
  isRefunded?: boolean;
  job_id?: string;
  tender_id?: string;
  transaction_count?: number;
}

interface PricingSettings {
  cost_per_credit_in_base: number;
}

interface ItemizedLedgerTableProps {
  clientId: string;
  transactions: JobRecord[];
  pricingSettings: PricingSettings | null;
}

const ItemizedLedgerTable: React.FC<ItemizedLedgerTableProps> = ({
  clientId,
  transactions,
  pricingSettings,
}) => {
  const theme = useTheme();
  const { account: user } = useAccount();

  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [teamsMap, setTeamsMap] = useState<Record<string, string>>({});

  // Pagination and filtering state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('itemizedLedgerRowsPerPage');
    return saved ? parseInt(saved, 10) : 25;
  });
  const [dateFilter, setDateFilter] = useState<'current' | 'previous' | 'custom'>('current');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    if (!clientId) {
      return;
    }

    const uMap: Record<string, string> = {};
    const tMap: Record<string, string> = {};

    Promise.all([
      getUsers({ companyId: clientId, limit: 1000 })
        .then((res) => {
          if (res.users) {
            for (const u of res.users) {
              uMap[u.uid] = u.displayName || u.email || u.uid || 'Unknown User';
            }
          }
        })
        .catch(() => {
          // ignore
        }),
      getTeamsByClient(clientId)
        .then((teams) => {
          for (const team of teams) {
            tMap[team.id] = team.teamName || team.id || 'Unknown Team';
          }
        })
        .catch(() => {
          // ignore
        }),
    ]).finally(() => {
      setUsersMap(uMap);
      setTeamsMap(tMap);
    });
  }, [clientId]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    localStorage.setItem('itemizedLedgerRowsPerPage', newRows.toString());
  };

  const formatBillingName = (name: string) => {
    return name
      .replace(/llm_generation\s*\(\s*/gi, '')
      .replace(/\)$/g, '')
      .replace(/llm_generation/gi, 'Generation')
      .replace(/workspace_initialization/gi, 'Tender Go Fee')
      .replace(/draft_response_gen/gi, 'Draft Response')
      .replace(/Draft Response Gen/gi, 'Draft Response')
      .replace(/draftResponse/gi, 'Draft Response')
      .replace(/alignment_analysis/gi, 'Capability Analysis')
      .replace(/alignmentAnalysis/gi, 'Capability Analysis')
      .replace(/capabilityPitch/gi, 'Capability Pitch')
      .replace(/visualExport/gi, 'Visual Export')
      .replace(/reviewResponse/gi, 'Review Response')
      .replace(/adhocDocumentGenerator/gi, 'Ad Hoc Document')
      .replace(/tenderDetailsExtractor/gi, 'Tender Extraction')
      .replace(/tenderInquiry/gi, 'Tender Inquiry')
      .replace(/quickReview/gi, 'Quick Review')
      .replace(/caseStudy/gi, 'Case Study Processing')
      .replace(/contextHunterIndex/gi, 'Deep Indexing')
      .replace(/context_hunter/gi, 'Document Search')
      .replace(/contextHunter/gi, 'Document Search')
      .replace(/people_cv/gi, 'Team Member Search')
      .replace(/cvProcessor/gi, 'CV Processing')
      .replace(/strictFormProcessor/gi, 'Strict Form Processing')
      .replace(/section_chat_validate/gi, 'Section Chat Validation')
      .replace(/^chat$/gi, 'Section Chat')
      .replace(/section_chat/gi, 'Section Chat')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/Ai Generation/gi, 'AI Generation')
      .trim();
  };

  const getRowColor = (actionType: string, contextTag?: string) => {
    const key = (contextTag || actionType).toLowerCase().replace(/\s+/g, '');
    const op = 0.2; // 20% opacity for better distinction without bleeding

    if (key.includes('draftresponse')) {
      return alpha('#f44336', op);
    } // Red
    if (key.includes('capabilityanalysis') || key.includes('alignmentanalysis')) {
      return alpha('#9c27b0', op);
    } // Purple
    if (key.includes('teammember') || key.includes('people_cv')) {
      return alpha('#00bcd4', op);
    } // Cyan
    if (key.includes('deepindexing') || key.includes('contexthunterindex')) {
      return alpha('#ff9800', op);
    } // Orange
    if (key.includes('documentsearch') || key.includes('contexthunter')) {
      return alpha('#4caf50', op);
    } // Green
    if (key.includes('cvprocessing') || key.includes('cvprocessor')) {
      return alpha('#795548', op);
    } // Brown
    if (key.includes('gofee') || key.includes('workspace_initialization')) {
      return alpha('#ffc107', op);
    } // Amber
    if (key.includes('quickreview')) {
      return alpha('#e91e63', op);
    } // Pink
    if (key.includes('reviewresponse')) {
      return alpha('#3f51b5', op);
    } // Indigo
    if (key.includes('adhoc')) {
      return alpha('#8bc34a', op);
    } // Light Green
    if (key.includes('extraction') || key.includes('extractor')) {
      return alpha('#009688', op);
    } // Teal
    if (key.includes('pitch')) {
      return alpha('#cddc39', op);
    } // Lime
    if (key.includes('visual')) {
      return alpha('#607d8b', op);
    } // Blue Grey
    if (key.includes('case')) {
      return alpha('#673ab7', op);
    } // Deep Purple
    if (key.includes('strictform')) {
      return alpha('#d32f2f', op);
    } // Dark Red
    if (key.includes('sectionchat')) {
      return alpha('#2196f3', op);
    } // Blue
    if (key.includes('generalchat')) {
      return alpha('#03a9f4', op);
    } // Light Blue
    if (key.includes('inquiry')) {
      return alpha('#8e24aa', op);
    } // Deep Purple Variant
    if (key.includes('generation')) {
      return alpha('#f06292', op);
    } // Pink Variant

    return alpha(theme.palette.text.secondary, op);
  };

  const resetIndex = transactions.findIndex((tx) => tx.action_type === 'rolling_cycle_reset');
  const currentCycleTransactions =
    resetIndex === -1 ? transactions : transactions.slice(0, resetIndex);

  const filteredTransactionsForLedger = useMemo(() => {
    if (dateFilter === 'current') {
      return currentCycleTransactions;
    }
    if (dateFilter === 'previous') {
      if (resetIndex === -1) {
        return [];
      }
      const secondResetIndex = transactions.findIndex(
        (tx, idx) => idx > resetIndex && tx.action_type === 'rolling_cycle_reset'
      );
      if (secondResetIndex === -1) {
        return transactions.slice(resetIndex + 1);
      }
      return transactions.slice(resetIndex + 1, secondResetIndex);
    }
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const start = dayjs(customStartDate).startOf('day').valueOf();
      const end = dayjs(customEndDate).endOf('day').valueOf();
      return transactions.filter((tx) => {
        let ts = 0;
        const tsValue = tx.startedAt || tx.updatedAt || tx.timestamp;
        if (
          tsValue &&
          typeof tsValue === 'object' &&
          'toDate' in tsValue &&
          typeof (tsValue as { toDate: () => Date }).toDate === 'function'
        ) {
          ts = (tsValue as { toDate: () => Date }).toDate().getTime();
        } else if (typeof tsValue === 'string' || typeof tsValue === 'number') {
          ts = new Date(tsValue).getTime();
        }
        return ts >= start && ts <= end;
      });
    }
    return transactions;
  }, [
    transactions,
    currentCycleTransactions,
    dateFilter,
    customStartDate,
    customEndDate,
    resetIndex,
  ]);

  const sortedJobs = useMemo(() => {
    const HIDDEN_CONTEXT_TAGS = ['section_chat', 'chat', 'tenderInquiry', 'section_chat_validate'];
    return filteredTransactionsForLedger
      .filter((job) => !(job.context_tag && HIDDEN_CONTEXT_TAGS.includes(job.context_tag)))
      .sort((a, b) => {
        const getMs = (tx: JobRecord) => {
          const tsValue = tx.startedAt || tx.updatedAt || tx.timestamp;
          if (
            tsValue &&
            typeof tsValue === 'object' &&
            'toDate' in tsValue &&
            typeof (tsValue as { toDate: () => Date }).toDate === 'function'
          ) {
            return (tsValue as { toDate: () => Date }).toDate().getTime();
          }
          if (typeof tsValue === 'string' || typeof tsValue === 'number') {
            return new Date(tsValue).getTime();
          }
          return Date.now();
        };
        return getMs(b) - getMs(a);
      });
  }, [filteredTransactionsForLedger]);

  if (!pricingSettings) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Itemized Ledger (Jobs)</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Billing Period</InputLabel>
            <Select
              value={dateFilter}
              label="Billing Period"
              onChange={(e) => {
                setDateFilter(e.target.value as 'current' | 'previous' | 'custom');
                setPage(0);
              }}
            >
              <MenuItem value="current">Current Billing Period</MenuItem>
              <MenuItem value="previous">Previous Billing Period</MenuItem>
              <MenuItem value="custom">Custom Date Range</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'custom' && (
            <>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Box>
      </Box>

      {sortedJobs.length === 0 ? (
        <Typography color="text.secondary">No recent transactions to display.</Typography>
      ) : (
        <>
          <StandardTable
            size="small"
            columns={[
              {
                id: 'dateTime',
                label: <strong>Date / Time</strong>,
                render: (job) => {
                  let dt: Date;
                  const tsValue = job.startedAt || job.updatedAt || job.timestamp;
                  if (
                    tsValue &&
                    typeof tsValue === 'object' &&
                    'toDate' in tsValue &&
                    typeof (tsValue as { toDate: () => Date }).toDate === 'function'
                  ) {
                    dt = (tsValue as { toDate: () => Date }).toDate();
                  } else if (typeof tsValue === 'string' || typeof tsValue === 'number') {
                    dt = new Date(tsValue);
                  } else {
                    dt = new Date();
                  }
                  return (
                    <Typography variant="body1">
                      {dayjs(dt).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  );
                },
              },
              {
                id: 'function',
                label: <strong>Function</strong>,
                render: (job) => {
                  const isGrouped = (job.transaction_count || 1) > 1;
                  const displayAction = job.context_tag || job.action_type;
                  const formatJobId = (jid?: string) => {
                    if (!jid) {
                      return null;
                    }
                    if (jid.startsWith('legacy_')) {
                      return 'Legacy Group';
                    }
                    if (jid.startsWith('sys_')) {
                      return 'System Task';
                    }
                    const parts = jid.split('_');
                    const uniquePart = parts.length > 1 ? parts[parts.length - 1] : jid;
                    if (!uniquePart) {
                      return null;
                    }
                    return uniquePart.length > 8 ? uniquePart.substring(0, 8) : uniquePart;
                  };
                  const displayJobId = formatJobId(job.job_id);
                  const displayTenderId = job.tender_id?.includes('_')
                    ? job.tender_id.substring(job.tender_id.indexOf('_') + 1)
                    : job.tender_id || (job.context_tag === 'people_cv' ? 'People Search' : '');

                  return (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {formatBillingName(displayAction)}
                        {isGrouped ? ` (${job.transaction_count || 1} items)` : ''}
                      </Typography>
                      {(job.job_id ||
                        job.tender_id ||
                        (!job.job_id && job.action_type === 'llm_generation')) && (
                        <Typography
                          variant="body2"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {job.job_id
                            ? `Job: ${displayJobId}`
                            : job.tender_id
                              ? `Tender: ${displayTenderId}`
                              : `Job: Legacy Group`}
                        </Typography>
                      )}
                    </>
                  );
                },
              },
              {
                id: 'teamUser',
                label: <strong>Team / User</strong>,
                render: (job) => {
                  const rawName = usersMap[job.user_id || ''];
                  let formattedUserName = rawName;
                  if (!rawName) {
                    if (job.user_id && job.user_id.length > 20) {
                      formattedUserName = 'Unknown User';
                    } else {
                      formattedUserName = job.user_id || 'System';
                    }
                  }
                  if (rawName?.includes(' ')) {
                    const parts = rawName.split(' ').filter(Boolean);
                    const lastPart = parts.length > 1 ? parts[parts.length - 1] : undefined;
                    if (parts.length > 1 && lastPart && lastPart.length > 0) {
                      formattedUserName = `${parts[0]} ${lastPart[0]}.`;
                    }
                  }

                  const rawTeamName = teamsMap[job.team_id || ''];
                  let formattedTeamName = rawTeamName;
                  if (!rawTeamName) {
                    if (job.team_id && job.team_id.length > 20) {
                      formattedTeamName = 'Unknown Team';
                    } else {
                      formattedTeamName = job.team_id || 'System';
                    }
                  }

                  return (
                    <>
                      <Typography variant="body1">{formattedTeamName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formattedUserName}
                      </Typography>
                    </>
                  );
                },
              },
              {
                id: 'cost',
                label: <strong>Cost</strong>,
                align: 'right',
                render: (job) => {
                  const allRefunded = job.isRefunded === true || job.status === 'refunded';
                  const totalCost =
                    (job.total_credits_burned || job.credits_burned || 0) *
                    (pricingSettings.cost_per_credit_in_base || 0);

                  return allRefunded ? (
                    <Typography
                      variant="body1"
                      sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                    >
                      $
                      {totalCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body1">
                      $
                      {totalCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  );
                },
              },
              {
                id: 'status',
                label: <strong>Status</strong>,
                align: 'center',
                render: (job) => {
                  return job.status === 'error' ? (
                    <Chip
                      label="Error"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: theme.palette.bidlogiq
                          ? theme.palette.bidlogiq.rust
                          : '#812A16',
                        color: 'white',
                      }}
                    />
                  ) : job.status === 'complete' ? (
                    <Chip label="Complete" color="success" sx={{ fontWeight: 'bold' }} />
                  ) : job.status === 'processing' ? (
                    <Chip label="Processing" color="warning" sx={{ fontWeight: 'bold' }} />
                  ) : job.job_id?.startsWith('legacy_') ? (
                    <Chip label="Legacy" color="default" sx={{ fontWeight: 'bold' }} />
                  ) : (
                    <Chip
                      label={job.status || 'Unknown'}
                      color="default"
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  );
                },
              },
              {
                id: 'action',
                label: <strong>Action</strong>,
                align: 'center',
                render: (job) => {
                  const formatJobId = (jid?: string) => {
                    if (!jid) {
                      return null;
                    }
                    if (jid.startsWith('legacy_')) {
                      return 'Legacy Group';
                    }
                    if (jid.startsWith('sys_')) {
                      return 'System Task';
                    }
                    const parts = jid.split('_');
                    const uniquePart = parts.length > 1 ? parts[parts.length - 1] : jid;
                    if (!uniquePart) {
                      return null;
                    }
                    return uniquePart.length > 8 ? uniquePart.substring(0, 8) : uniquePart;
                  };
                  const displayJobId = formatJobId(job.job_id);
                  const displayTenderId = job.tender_id?.includes('_')
                    ? job.tender_id.substring(job.tender_id.indexOf('_') + 1)
                    : job.tender_id || (job.context_tag === 'people_cv' ? 'People Search' : '');

                  const disputeSubject = encodeURIComponent(
                    `Billing Dispute: Job ${displayJobId || displayTenderId || 'Unknown'}`
                  );
                  const totalCost =
                    (job.total_credits_burned || job.credits_burned || 0) *
                    (pricingSettings.cost_per_credit_in_base || 0);

                  const disputeBody = encodeURIComponent(
                    `Hello BIDLOGIQ Support,\n\n` +
                      `I would like to dispute a recent generative job.\n\n` +
                      `User: ${user?.displayName || user?.email || 'Unknown'}\n` +
                      `Client: ${user?.clientName || clientId}\n` +
                      `Job ID: ${job.job_id || 'Unknown'}\n` +
                      `Tender ID: ${displayTenderId || 'Unknown'}\n` +
                      `Cost: $${totalCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}\n\n` +
                      `Reason for dispute:\n[Please describe the issue here]\n\n` +
                      `Thank you,\n${user?.displayName || 'User'}`
                  );

                  const allRefunded = job.isRefunded === true || job.status === 'refunded';

                  return allRefunded ? (
                    <Chip label="Refunded" color="default" sx={{ fontWeight: 'bold' }} />
                  ) : job.status === 'complete' && totalCost > 0 ? (
                    <Button
                      size="small"
                      variant="contained"
                      href={`mailto:support@bidlogiq.com?subject=${disputeSubject}&body=${disputeBody}`}
                      sx={{
                        backgroundColor: 'bidlogiq.rust',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#b4330b' : '#8d2809',
                        },
                      }}
                    >
                      Dispute
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
            ]}
            data={sortedJobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
            keyExtractor={(job) => job.id}
            rowSx={(job) => ({ backgroundColor: getRowColor(job.action_type, job.context_tag) })}
          />
          <TablePagination
            component="div"
            count={sortedJobs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}
    </>
  );
};

export default ItemizedLedgerTable;
